import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Sync as SyncIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LeadCampaignApi from "../../../../../infrastructure/api/leadCampaignApi";
import AdminActionConfirmDialog from "../components/AdminActionConfirmDialog";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import LeadContactGroupFormDialog from "./components/LeadContactGroupFormDialog";

const LeadContactGroupsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await LeadCampaignApi.getGroups({ page: 1, pageSize: 100 });
      const { error: err, data } = LeadCampaignApi.normalize(res, "بارگذاری گروه‌ها ناموفق بود.");
      if (err) {
        setError(err);
        setItems([]);
        return;
      }
      setItems(data?.items || data?.Items || []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        statusLabel: row.isActive ? "فعال" : "غیرفعال",
        contactCountLabel: row.contactCount ?? 0,
      })),
    [items]
  );

  const headCells = useMemo(
    () => [
      { id: "title", label: "عنوان" },
      { id: "sourceName", label: "منبع" },
      { id: "contactCountLabel", label: "تعداد مخاطب", type: "number" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
    ],
    []
  );

  const toggleStatus = async (row) => {
    try {
      const res = await LeadCampaignApi.patchGroupStatus(row.id, !row.isActive);
      const normalized = LeadCampaignApi.normalize(res, "تغییر وضعیت ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success("وضعیت گروه به‌روزرسانی شد.");
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await LeadCampaignApi.deleteGroup(deleteTarget.id);
      const normalized = LeadCampaignApi.normalize(res, "حذف گروه ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success("گروه حذف شد.");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا");
    } finally {
      setDeleting(false);
    }
  };

  const tableActions = useMemo(
    () => [
      {
        icon: <ViewIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => navigate(`/SLN/MarketGrowth/LeadCampaigns/Groups/${row.id}`),
        color: "primary",
        tooltip: "مشاهده",
      },
      {
        icon: <EditIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => {
          setEditingItem(row);
          setDialogOpen(true);
        },
        color: "secondary",
        tooltip: "ویرایش",
      },
      {
        icon: <DeleteIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDeleteTarget(row),
        color: "error",
        tooltip: "حذف",
      },
      {
        icon: <PowerSettingsNewIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: toggleStatus,
        color: "info",
        tooltip: "تغییر وضعیت",
      },
    ],
    [navigate]
  );

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = editingItem
        ? await LeadCampaignApi.updateGroup(editingItem.id, payload)
        : await LeadCampaignApi.createGroup(payload);
      const normalized = LeadCampaignApi.normalize(
        res,
        editingItem ? "ویرایش گروه ناموفق بود." : "ایجاد گروه ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      toast.success(editingItem ? "گروه ویرایش شد." : "گروه ایجاد شد.");
      setDialogOpen(false);
      setEditingItem(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  return (
    <MarketGrowthPageShell
      title="گروه‌های مخاطب"
      description="مدیریت گروه‌های شماره موبایل برای کمپین‌های جذب."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingItem(null);
            setDialogOpen(true);
          }}
        >
          گروه جدید
        </Button>
        <IconButton onClick={load}>
          <SyncIcon />
        </IconButton>
      </Box>
      <Card>
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            defaultSort={{ orderBy: "title", order: "asc" }}
          />
        </CardContent>
      </Card>
      <LeadContactGroupFormDialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingItem}
        saving={saving}
      />
      <AdminActionConfirmDialog
        open={Boolean(deleteTarget)}
        title="حذف گروه مخاطب"
        description={
          deleteTarget
            ? `آیا از حذف گروه «${deleteTarget.title}» مطمئن هستید؟`
            : ""
        }
        confirmLabel="حذف"
        confirmColor="error"
        showNotes={false}
        saving={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </MarketGrowthPageShell>
  );
};

export default LeadContactGroupsPage;
